import { useCallback } from "react";

import { DataEditorProps, GridCellKind } from "@glideapps/glide-data-grid";
import { useAtomValue, useSetAtom } from "jotai";

import {
  changedCellsAtom,
  columnsAtom,
  filteredUwisAtom
} from "../atoms/fieldGroupAtoms";
import { addCellsToChangeMap } from "../atoms/utils/cellTransformers";
import { useEnrichColumnWithPythonScript } from "./useEnrichColumnWithPythonScript";

export function useGridCellEdit() {
  const columns = useAtomValue(columnsAtom);
  const uwis = useAtomValue(filteredUwisAtom);
  const setColumns = useSetAtom(columnsAtom);
  const setChangedCells = useSetAtom(changedCellsAtom);
  const { enrichColumns } = useEnrichColumnWithPythonScript();

  const enrichedColumns = enrichColumns(columns);

  const onCellsEdited = useCallback<NonNullable<DataEditorProps["onCellsEdited"]>>(
    (newValues) => {
      if (newValues.length === 0) return false;

      const columnsToUpdate = new Map<number, Map<string, string>>();
      const changes: Array<{ propertyName: string; uwi: string; value: string }> = [];

      newValues.forEach(({ location, value }) => {
        const [col, row] = location;
        const uwi = uwis[row];
        if (!uwi || col === 0) return;

        const dataColIndex = col - 1;
        if (dataColIndex < 0 || dataColIndex >= enrichedColumns.length) return;

        const column = enrichedColumns[dataColIndex];
        if (!column) return;

        const newData = value.kind === GridCellKind.Text ? value.data : "";

        if (!columnsToUpdate.has(dataColIndex)) {
          columnsToUpdate.set(dataColIndex, new Map());
        }
        const columnMap = columnsToUpdate.get(dataColIndex);
        if (columnMap) {
          columnMap.set(uwi, newData);
        }

        changes.push({ propertyName: column.propertyName, uwi, value: newData });
      });

      if (columnsToUpdate.size === 0) {
        return false;
      }

      // Track changes using utility
      setChangedCells((prev) => addCellsToChangeMap(prev, changes));

      const updatedColumns = columns.map((col, index) => {
        const columnUpdates = columnsToUpdate.get(index);
        if (!columnUpdates) return col;

        const newData = { ...col.data };
        columnUpdates.forEach((value, uwi) => {
          newData[uwi] = value;
        });

        return { ...col, data: newData };
      });

      setColumns(updatedColumns);

      return true;
    },
    [enrichedColumns, uwis, setColumns, setChangedCells]
  );

  return { onCellsEdited };
}
